import { useCallback, useEffect, useRef } from "react";
import { CURSOR_THROTTLE_MS } from "./constants";

/**
 * ── Live cursors ──
 * Stream our pointer over the board to everyone else, throttled. `onCursor` is read through a
 * ref so the throttled sender stays stable; `lastCursorRef` timestamps the last emit (reset to 0
 * on a clear, so the next entry sends immediately). Received cursors render via `pixiCombatBoard.ts`'s
 * `setCursors`.
 */
export function useCursorStream(onCursor: ((at: { x: number; y: number } | null) => void) | undefined) {
  const onCursorRef = useRef(onCursor);
  onCursorRef.current = onCursor;
  const lastCursorRef = useRef(0);
  const streamCursor = useCallback((world: { x: number; y: number } | null) => {
    if (!onCursorRef.current) return;
    if (world === null) {
      lastCursorRef.current = 0; // a clear always goes, and primes the next entry to send at once
      onCursorRef.current(null);
      return;
    }
    const now = Date.now();
    if (now - lastCursorRef.current < CURSOR_THROTTLE_MS) return;
    lastCursorRef.current = now;
    onCursorRef.current(world);
  }, []);
  // Clear our cursor for everyone if this surface unmounts mid-hover (e.g. leaving COMBAT.app
  // without the socket closing first) — no lingering ghost cursor.
  useEffect(() => () => onCursorRef.current?.(null), []);

  return streamCursor;
}
